import {
  Directive,
  Input,
  TemplateRef,
  ViewContainerRef,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PermissionService } from '@dal/core';

/**
 * Structural directive to show/hide elements based on user having ANY of the specified permissions.
 *
 * Usage:
 * ```html
 * <button *dalHasAnyPermission="['Edit Metadata', 'Delete Assets']">
 *   Edit or Delete
 * </button>
 * ```
 *
 * The element will only be rendered in the DOM if the user has at least one of the specified permissions.
 */
@Directive({
  selector: '[dalHasAnyPermission]',
})
export class HasAnyPermissionDirective implements OnInit, OnDestroy {
  private permissions: string[] = [];
  private destroy$ = new Subject<void>();

  constructor(
    private templateRef: TemplateRef<void>,
    private viewContainer: ViewContainerRef,
    private permissionService: PermissionService
  ) {}

  /**
   * Set the permissions to check. The element will be shown if the user has ANY of these permissions.
   *
   * @param permissions Array of permission names to check
   */
  @Input()
  set dalHasAnyPermission(permissions: string[]) {
    this.permissions = permissions;
    this.updateView();
  }

  ngOnInit(): void {
    // Subscribe to permission changes to dynamically update the view
    this.permissionService.getPermissions$().pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateView();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Update the view based on whether the user has any of the required permissions.
   */
  private updateView(): void {
    const shouldShow = this.permissionService.hasAnyPermission(this.permissions);

    if (shouldShow) {
      if (this.viewContainer.length === 0) {
        this.viewContainer.createEmbeddedView(this.templateRef);
      }
      return;
    }

    this.viewContainer.clear();
  }
}
